import * as React from 'react';
import {
    Alert, Box, Breadcrumbs, Button, Card, CardContent, Chip, CircularProgress, Link as MuiLink, Stack, TextField, Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { AI_LABELS, deleteAiConversation, getAiConversation, getAiConversationBySubject, getAiConversations } from '../../Api';
import { AiConversationDetailDto, AiConversationMessageDto, AiConversationSummaryDto } from '../../models/dtos/AiDto';
import { aiLoadErrorText, formatAiDate, JsonBlock, StatLine, TextBlock } from '../../components/ai/AiShared';
import { ConfirmDialog } from '../../components/betupFeed/BetupFeedShared';
import { SupportPhotoGrid } from '../../components/SupportPhotos';
import { isSupportPhotoUrl } from '../../utils/supportPhotos';

/** Conversations of delegated agents (e.g. Betty per support chat): the agent's stored memory of each thread. */
export default function AiConversationsPage() {
    const navigate = useNavigate();
    const [agentCode, setAgentCode] = React.useState('');
    const [endUserId, setEndUserId] = React.useState('');
    const [subjectId, setSubjectId] = React.useState('');
    const [debounced, setDebounced] = React.useState({ agentCode: '', endUserId: '', subjectId: '' });
    const [rows, setRows] = React.useState<AiConversationSummaryDto[]>([]);
    const [rowCount, setRowCount] = React.useState(0);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [reloadKey, setReloadKey] = React.useState(0);
    const [paginationModel, setPaginationModel] = React.useState<GridPaginationModel>({ page: 0, pageSize: 25 });

    React.useEffect(() => {
        const timer = window.setTimeout(() => setDebounced({ agentCode: agentCode.trim(), endUserId: endUserId.trim(), subjectId: subjectId.trim() }), 400);
        return () => window.clearTimeout(timer);
    }, [agentCode, endUserId, subjectId]);

    React.useEffect(() => { setPaginationModel(p => (p.page === 0 ? p : { ...p, page: 0 })); }, [debounced]);

    const load = React.useCallback(async () => {
        setLoading(true);
        const page = await getAiConversations({
            agentCode: debounced.agentCode || undefined,
            endUserId: debounced.endUserId || undefined,
            subjectId: debounced.subjectId || undefined,
        }, paginationModel.page * paginationModel.pageSize, paginationModel.pageSize);
        setLoading(false);
        if (page == null) {
            setError(aiLoadErrorText(AI_LABELS.conversations, 'Failed to load conversations.'));
            setRows([]);
            setRowCount(0);
            return;
        }
        setError(null);
        setRows(page.items ?? []);
        setRowCount(page.totalItems ?? 0);
    }, [debounced, paginationModel]);

    React.useEffect(() => { load(); }, [load, reloadKey]);

    const columns: GridColDef<AiConversationSummaryDto>[] = [
        { field: 'lastMessageAt', headerName: 'Last message', width: 170, sortable: false, valueFormatter: ({ value }) => formatAiDate(value as string) },
        { field: 'agentCode', headerName: 'Agent', width: 170, sortable: false },
        {
            field: 'subject', headerName: 'Thread', width: 200, sortable: false,
            valueGetter: ({ row }) => `${row.subjectType} / ${row.subjectId}`,
        },
        { field: 'endUserId', headerName: 'End user', width: 110, sortable: false, valueFormatter: ({ value }) => (value == null ? '—' : `#${value}`) },
        { field: 'messageCount', headerName: 'Items', width: 80, sortable: false },
        {
            field: 'hasSummary', headerName: 'Summary', width: 100, sortable: false,
            renderCell: ({ row }) => (row.hasSummary ? <Chip size="small" label="yes" color="info" variant="outlined" /> : null),
        },
        { field: 'caller', headerName: 'Caller', width: 170, sortable: false },
        { field: 'createdAt', headerName: 'Started', width: 170, sortable: false, valueFormatter: ({ value }) => formatAiDate(value as string) },
    ];

    return (
        <Stack gap={2} sx={{ mx: 5, my: 5 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                    <Typography variant="h4">AI conversations</Typography>
                    <Typography variant="body2" color="text.secondary">
                        What an agent remembers of each thread (e.g. Betty per support chat): every message once, plus its tool calls,
                        results and replies. Kept 90 days after the last message.
                    </Typography>
                </Box>
                <Button startIcon={<RefreshIcon />} onClick={() => setReloadKey(k => k + 1)}>Refresh</Button>
            </Stack>
            <Stack direction="row" gap={1.5} flexWrap="wrap">
                <TextField size="small" label="Agent code" value={agentCode} onChange={e => setAgentCode(e.target.value)} />
                <TextField size="small" label="End user id" value={endUserId} onChange={e => setEndUserId(e.target.value.replace(/\D/g, ''))} />
                <TextField size="small" label="Thread id (e.g. chat id)" value={subjectId} onChange={e => setSubjectId(e.target.value)} />
            </Stack>
            {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
            <DataGrid
                autoHeight
                rows={rows}
                columns={columns}
                getRowId={row => row.conversationId}
                paginationMode="server"
                rowCount={rowCount}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                pageSizeOptions={[10, 25, 50, 100]}
                loading={loading}
                disableRowSelectionOnClick
                disableColumnMenu
                onRowClick={({ row }) => navigate(`/ai/conversations/${encodeURIComponent((row as AiConversationSummaryDto).conversationId)}`)}
                sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
                localeText={{ noRowsLabel: 'No conversations' }}
            />
        </Stack>
    );
}

const ROLE_STYLE: Record<string, { label: string; align: 'flex-start' | 'flex-end'; bg: string }> = {
    USER: { label: 'User', align: 'flex-start', bg: 'action.hover' },
    OPERATOR: { label: 'Human operator', align: 'flex-end', bg: 'warning.light' },
    ASSISTANT: { label: 'Agent', align: 'flex-end', bg: 'primary.light' },
    TOOL: { label: 'Tool results', align: 'flex-end', bg: 'transparent' },
};

function ConversationItem({ item, summarized }: { item: AiConversationMessageDto; summarized: boolean }) {
    const style = ROLE_STYLE[item.role] ?? ROLE_STYLE.USER;
    const toolCalls = item.role === 'ASSISTANT' ? item.blocks ?? [] : [];
    const results = item.role === 'TOOL' ? item.blocks ?? [] : [];
    // A user's photos the agent was shown; only our own uploads are drawn (utils/supportPhotos.ts).
    const photos = item.role === 'USER'
        ? (item.blocks ?? []).filter(b => b.type === 'image' && b.url && isSupportPhotoUrl(b.url)).map(b => b.url as string)
        : [];
    return (
        <Stack alignItems={style.align} sx={{ opacity: summarized ? 0.55 : 1 }}>
            <Stack direction="row" gap={1} alignItems="center" sx={{ mb: 0.25 }}>
                <Typography variant="caption" color="text.secondary">#{item.seq} · {style.label} · {formatAiDate(item.sentAt)}</Typography>
                {item.isReply && <Chip size="small" label="reply" color="success" variant="outlined" />}
                {summarized && <Chip size="small" label="in summary" variant="outlined" />}
                {item.runId && (
                    <MuiLink component={RouterLink} to={`/ai/runs/${encodeURIComponent(item.runId)}`} underline="hover" variant="caption">
                        run {item.runId.slice(0, 8)}…
                    </MuiLink>
                )}
                {item.externalMessageId && <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{item.externalMessageId}</Typography>}
            </Stack>
            {item.content && (
                <Box sx={{ bgcolor: style.bg, borderRadius: 2, px: 1.5, py: 1, maxWidth: '75%', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    <Typography variant="body2">{item.content}</Typography>
                </Box>
            )}
            {photos.length > 0 && <SupportPhotoGrid urls={photos} size={96} />}
            {toolCalls.map(call => (
                <Box key={call.id} sx={{ border: 1, borderColor: 'divider', borderRadius: 2, px: 1.5, py: 1, mt: 0.5, maxWidth: '75%', width: '100%' }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>⚙ {call.name}</Typography>
                    <JsonBlock value={call.input} maxHeight={160} />
                </Box>
            ))}
            {results.map(result => (
                <Box key={result.toolUseId} sx={{ border: 1, borderColor: result.isError ? 'error.main' : 'divider', borderRadius: 2, px: 1.5, py: 1, mt: 0.5, maxWidth: '75%', width: '100%' }}>
                    <Typography variant="caption" color={result.isError ? 'error' : 'text.secondary'}>
                        {result.isError ? 'Error' : 'Result'} · {result.toolUseId}
                    </Typography>
                    <TextBlock text={result.content} maxHeight={180} />
                </Box>
            ))}
        </Stack>
    );
}

/** The support agent whose conversation a support chat opens (/ai/conversations/support-chat/:chatId). */
export const SUPPORT_AGENT_CODE = 'support.betty';

export function AiConversationPage() {
    // Either the hub's id (/ai/conversations/:conversationId) or a support chat (/ai/conversations/support-chat/:chatId).
    const { conversationId: idParam, chatId } = useParams<{ conversationId?: string; chatId?: string }>();
    const navigate = useNavigate();
    const [conversation, setConversation] = React.useState<AiConversationDetailDto | null>(null);
    const [error, setError] = React.useState<string | null>(null);
    const [notFound, setNotFound] = React.useState(false);
    const [loading, setLoading] = React.useState(true);
    const [confirmDelete, setConfirmDelete] = React.useState(false);
    const conversationId = conversation?.conversationId ?? idParam;

    const load = React.useCallback(async () => {
        if (!idParam && !chatId) return;
        setLoading(true);
        setNotFound(false);
        if (chatId) {
            const result = await getAiConversationBySubject(SUPPORT_AGENT_CODE, 'support_chat', chatId);
            setLoading(false);
            if (result.status === 404) {
                setNotFound(true);
                setError(null);
                return;
            }
            if (result.error || !result.data) {
                setError(`Failed to load the AI conversation of chat ${chatId}: ${result.error ?? 'empty response'}`);
                return;
            }
            setError(null);
            setConversation(result.data);
            return;
        }
        const detail = await getAiConversation(idParam!);
        setLoading(false);
        if (detail == null) {
            setError(aiLoadErrorText(AI_LABELS.conversation, `Failed to load conversation ${idParam}.`));
            return;
        }
        setError(null);
        setConversation(detail);
    }, [idParam, chatId]);

    React.useEffect(() => { load(); }, [load]);

    const remove = async () => {
        if (!conversationId) return;
        setConfirmDelete(false);
        const result = await deleteAiConversation(conversationId);
        if (result.error) {
            setError(result.error);
            return;
        }
        navigate('/ai/conversations');
    };

    return (
        <Stack gap={2} sx={{ mx: 5, my: 4 }}>
            <Breadcrumbs>
                <MuiLink component={RouterLink} to="/ai" underline="hover">AI</MuiLink>
                <MuiLink component={RouterLink} to="/ai/conversations" underline="hover">Conversations</MuiLink>
                <Typography color="text.primary" sx={{ fontFamily: 'monospace' }}>{chatId ? `support chat ${chatId}` : conversationId}</Typography>
            </Breadcrumbs>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h5">
                    {conversation ? `${conversation.agentCode} · ${conversation.subjectType} ${conversation.subjectId}` : 'Conversation'}
                </Typography>
                <Stack direction="row" gap={1}>
                    <Button startIcon={<RefreshIcon />} onClick={load}>Refresh</Button>
                    <Button color="error" startIcon={<DeleteOutlineIcon />} disabled={!conversation} onClick={() => setConfirmDelete(true)}>Delete</Button>
                </Stack>
            </Stack>
            {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
            {notFound && (
                <Alert severity="info">
                    No AI conversation for support chat {chatId} yet: {SUPPORT_AGENT_CODE} has not answered it through the AI hub
                    (the chat may predate the switch, or go straight to a human).
                </Alert>
            )}
            {loading && conversation == null && <CircularProgress />}
            {conversation && (
                <>
                    <Card variant="outlined">
                        <CardContent>
                            <Stack direction="row" gap={4} flexWrap="wrap">
                                <StatLine label="End user" value={conversation.endUserId != null
                                    ? <MuiLink component={RouterLink} to={`/users/${conversation.endUserId}`} underline="hover">#{conversation.endUserId}</MuiLink>
                                    : '—'} />
                                <StatLine label="Caller" value={conversation.caller ?? '—'} />
                                <StatLine label="Items" value={conversation.messages.length} />
                                <StatLine label="Started" value={formatAiDate(conversation.createdAt)} />
                                <StatLine label="Last message" value={formatAiDate(conversation.lastMessageAt)} />
                            </Stack>
                            {conversation.summary && (
                                <Box sx={{ mt: 1.5 }}>
                                    <Typography variant="subtitle2">Summary (replaces items up to #{conversation.summarizedUntilSeq})</Typography>
                                    <TextBlock text={conversation.summary} maxHeight={220} />
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                    <Stack gap={1.25}>
                        {conversation.messages.map(item => (
                            <ConversationItem key={item.messageId} item={item}
                                summarized={conversation.summarizedUntilSeq != null && item.seq <= conversation.summarizedUntilSeq} />
                        ))}
                    </Stack>
                </>
            )}
            <ConfirmDialog
                open={confirmDelete}
                title="Delete conversation?"
                text="The agent forgets this thread: its next run starts from the messages the caller sends."
                confirmLabel="Delete"
                confirmColor="error"
                onClose={() => setConfirmDelete(false)}
                onConfirm={remove}
            />
        </Stack>
    );
}
